import type { Project } from '../types/portal'

/**
 * Deliverable order is the one piece of project structure Portal staff arrange
 * by hand, so it is kept in browser storage and restored on load. Everything
 * else in the prototype lives in memory.
 */
const KEY = 'portal.deliverableOrder'

type OrderMap = Record<string, string[]>

function read(): OrderMap {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(KEY)
    return raw ? (JSON.parse(raw) as OrderMap) : {}
  } catch {
    return {}
  }
}

export function saveDeliverableOrder(projectId: string, ids: string[]): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ ...read(), [projectId]: ids }))
  } catch {
    /* storage unavailable — order simply reverts on reload */
  }
}

/** Re-applies any saved order, keeping unknown or newly added items in place. */
export function applyStoredOrder(projects: Project[]): Project[] {
  const stored = read()
  if (!Object.keys(stored).length) return projects

  return projects.map((project) => {
    const order = stored[project.id]
    if (!order?.length) return project

    const ranked = [...project.deliverables].sort((a, b) => {
      const left = order.indexOf(a.id)
      const right = order.indexOf(b.id)
      if (left === -1 && right === -1) return 0
      if (left === -1) return 1
      if (right === -1) return -1
      return left - right
    })

    return { ...project, deliverables: ranked }
  })
}
